import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "../locales/en/common.json";

// Suppress i18next Locize advertisement in console before i18next init runs.
if (typeof window !== "undefined") {
  const origWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    if (typeof args[0] === "string" && args[0].includes("locize")) return;
    origWarn.apply(console, args);
  };
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { common: en } },
    defaultNS: "common",
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    detection: { order: ["localStorage", "navigator"] },
    saveMissing: false,
    missingKeyHandler: false,
  });

export default i18n;

export function formatDate(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    dateStyle: "medium",
  }).format(d);
}

export function formatDateUtc(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

export function formatDateTime(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

export function formatMonthYear(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

export function formatLocalMonthYear(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    month: "long",
    year: "numeric",
  }).format(d);
}

export function formatShortMonthYear(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

export function formatMonthDay(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(d);
}

export function formatLocalMonthDay(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    month: "short",
    day: "numeric",
  }).format(d);
}

export function formatLocalMonthDayYear(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

export function formatWeekday(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    weekday: "short",
    timeZone: "UTC",
  }).format(d);
}

export function formatLocalWeekday(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    weekday: "short",
  }).format(d);
}

export function formatMonthDayTime(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function formatWeekdayMonthDay(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(d);
}

export function formatWeekdayDay(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    weekday: "short",
    day: "numeric",
  }).format(d);
}

export function formatLongWeekdayMonthDay(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(d);
}

export function formatLongWeekdayMonthDayYear(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

export function formatTime(date: string | Date, locale?: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale ?? i18n.language, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function formatNumber(value: number, locale?: string): string {
  return new Intl.NumberFormat(locale ?? i18n.language).format(value);
}

export function formatCurrency(
  cents: number,
  currency = "EUR",
  optionsOrLocale?: Intl.NumberFormatOptions | string,
  locale?: string,
): string {
  const options = typeof optionsOrLocale === "string" ? undefined : optionsOrLocale;
  const resolvedLocale = typeof optionsOrLocale === "string" ? optionsOrLocale : locale;
  return new Intl.NumberFormat(resolvedLocale ?? i18n.language, {
    style: "currency",
    currency,
    ...options,
  }).format(cents / 100);
}

export function formatPercent(value: number, locale?: string): string {
  return new Intl.NumberFormat(locale ?? i18n.language, {
    style: "percent",
    maximumFractionDigits: 1,
  }).format(value / 100);
}

export function formatRelativeTime(
  date: string | Date,
  locale?: string,
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const diff = d.getTime() - Date.now();
  const absDiff = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(locale ?? i18n.language, {
    numeric: "auto",
  });

  if (absDiff < 60_000) {
    return rtf.format(Math.round(diff / 1000), "second");
  }
  if (absDiff < 3_600_000) {
    return rtf.format(Math.round(diff / 60_000), "minute");
  }
  if (absDiff < 86_400_000) {
    return rtf.format(Math.round(diff / 3_600_000), "hour");
  }
  return rtf.format(Math.round(diff / 86_400_000), "day");
}
